/**
 * FilterDropdown — a curved, on-brand replacement for native <select>.
 * Used by the conversation sidebar to filter by department / designation.
 * Renders the menu in a portal so it can never be clipped by an
 * overflow ancestor.
 */
import * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

export interface FilterOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  searchable?: boolean;
}

export default function FilterDropdown({
  value, options, onChange, placeholder = 'Select…', ariaLabel, searchable = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null);

  const current = options.find(o => o.value === value);

  useLayoutEffect(() => {
    if (!open) { setAnchor(null); return; }
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      setAnchor({ top: r.bottom + 6, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Clear the search box each time the menu closes.
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  // Filter options by the search box. All matches render; the list lives in a
  // scroll area capped to ~5 rows (see the menu below), so the box stays short
  // but you can scroll to the rest instead of hitting a hard cut-off.
  const q = query.trim().toLowerCase();
  const resetOption = options.find(o => o.value === '');
  const items = options.filter(o => o.value !== '');
  const matched = q ? items.filter(o => o.label.toLowerCase().includes(q)) : items;

  const renderOption = (opt: FilterOption) => {
    const isActive = opt.value === value;
    return (
      <button
        key={opt.value || '__all__'}
        type="button"
        role="option"
        aria-selected={isActive}
        onClick={() => { onChange(opt.value); setOpen(false); }}
        className={`gc-filter-option ${isActive ? 'is-active' : ''} w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-[10px] transition-colors`}
      >
        <span aria-hidden className="gc-filter-option__check-slot">
          {isActive && <Check size={13} strokeWidth={2.5} />}
        </span>
        <span className="truncate flex-1">{opt.label}</span>
      </button>
    );
  };

  const menuPositionVars = anchor
    ? ({
        position: 'fixed',
        top: anchor.top,
        left: anchor.left,
        width: Math.max(180, anchor.width),
      } as React.CSSProperties)
    : undefined;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`gc-filter-trigger ${open ? 'is-open' : ''} w-full flex items-center justify-between text-[11.5px] font-medium px-3 py-2 transition-all focus:outline-none focus:ring-2`}
      >
        <span className={`gc-filter-trigger__label ${current && current.value ? 'is-selected' : ''} truncate`}>
          {current?.label ?? placeholder}
        </span>
        <ChevronDown
          size={13}
          className={`gc-filter-trigger__chevron ${open ? 'is-open' : ''}`}
        />
      </button>

      {open && anchor && createPortal(
        <>
          <div
            className="gc-filter-backdrop"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            role="listbox"
            aria-label={ariaLabel}
            className="gc-filter-menu"
            style={menuPositionVars}
          >
            {searchable && (
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                aria-label={`Search ${ariaLabel || 'options'}`}
                className="gc-filter-search w-full text-[11.5px] px-2.5 py-1.5 mb-1 rounded-[8px] outline-none"
                style={{
                  background: 'rgba(148,163,184,0.12)',
                  border: '1px solid rgba(148,163,184,0.28)',
                  color: 'inherit',
                }}
              />
            )}
            {/* Options scroll independently — ~5 rows tall so the box stays short,
                while the search box above stays pinned. */}
            <div className="gc-scroll" style={{ maxHeight: 216, overflowY: 'auto' }}>
              {resetOption && renderOption(resetOption)}
              {matched.map(renderOption)}
              {q && matched.length === 0 && (
                <div className="px-2.5 py-2 text-[11.5px]" style={{ opacity: 0.6 }}>
                  No matches
                </div>
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
